// src/pages/Patientsappointment.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Container, Col, Button, Card, Alert, OverlayTrigger, Tooltip, Table } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import {
  getPatientById,
  getAccountByPatientId,
  getAppointmentById,
} from "../api";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';
import { getBookingsReturnPath } from "../utils/bookingsReturn";
import { canViewAccounts } from "../utils/permissions";
import { getAgedBalancesByAccount } from "../afinanceapi";

import { useEditPatientModal } from "../Components/PatientsAccounts/EditPatients";
import CheckEligibility from "../Components/PatientsAccounts/CheckEligibility";
import { EligibilitySummary } from "../Components/PatientsAccounts/CheckEligibility";
import Session from "../Components/Sessions/Session";
import CaseManagerPanel from "../Components/Cases/CaseModal";
import { useRecordingActions } from "../Components/SyncTo/RecordingProvider";
import { userCanSeeClinicalHistory } from "../Components/ClinicalHistory/access";
import "../Components/Accounts/acc-list.css";
import "../Components/Accounts/acc-rail.css";

const EMPTY_REFERRERS = [];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16).replace("T", " ");
};

const formatBreadcrumbDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const ROW = ({ icon, children }) => (
  <div className="mb-1" style={{ lineHeight: "1.25" }}>
    {icon && <i className={`${icon} me-2 text-primary`} />}
    {children}
  </div>
);

function PatientInfoCard({
  patient,
  account,
  agedBuckets,
  unallocatedBalance,
  canViewBalance,
  canOpenAccount,
  appointmentId,
  appointment,
  id,
  locationState,
  navigate,
}) {
  if (!patient) return <p>Loading patient information...</p>;

  return (
    <>
      {/* Col 2 — Patient details */}
      <Col xs={12} md={3} className="mb-3 mb-md-0">
        {patient?.patientNumber && <ROW icon="fa-regular fa-hashtag">{patient.patientNumber}</ROW>}
        {patient?.patientIdNumber && <ROW icon="fa-solid fa-id-card">{patient.patientIdNumber}</ROW>}
        {patient?.patientCellNumber && <ROW icon="fas fa-phone-alt">{patient.patientCellNumber}</ROW>}
        {patient?.patientEmail && <ROW icon="fas fa-envelope">{patient.patientEmail}</ROW>}
        {patient?.patientGender && (
          <ROW icon="fa-solid fa-venus-mars">
            {patient.patientGender === "1" ? "Male" : patient.patientGender === "2" ? "Female" : "Unspecified"}
          </ROW>
        )}
        {patient?.patientHomeLanguage && <ROW icon="fa fa-language">{patient.patientHomeLanguage}</ROW>}
      </Col>

      {/* Col 3 — Medical aid / IOD */}
      <Col xs={12} md={3} className="mb-3 mb-md-0">
        {patient?.patientIODFund ? (
          <>
            <ROW>{patient.patientIODFund}</ROW>
            {(patient.patientIODPlan || patient.patientIODOption) && (
              <ROW>
                <span className="fw-semibold">{patient.patientIODPlan || "-"}</span>
                {patient.patientIODOption && (
                  <> <span className="text-muted">|</span> <span className="fw-semibold">{patient.patientIODOption}</span></>
                )}
              </ROW>
            )}
            {patient?.patientIODClaimRefNumber && <ROW><strong>Claim Ref #:</strong> {patient.patientIODClaimRefNumber}</ROW>}
            {patient?.patientIODEmployer && <ROW><strong>Employer:</strong> {patient.patientIODEmployer}</ROW>}
            {patient?.patientIODEmployerWCARegNum && <ROW><strong>WCA Reg #:</strong> {patient.patientIODEmployerWCARegNum}</ROW>}
            {patient?.patientIODDateOfInjury && <ROW><strong>Date of Injury:</strong> {patient.patientIODDateOfInjury}</ROW>}
          </>
        ) : patient?.patientMedicalAid ? (
          <>
            <ROW><span className="fw-semibold">{patient.patientMedicalAid}</span></ROW>
            <ROW>
              <span className="fw-semibold">{patient.patientMedicalAidPlan}</span>
              {" "}<span className="text-muted">|</span>{" "}
              <span className="fw-semibold">{patient.patientMedicalAidOption}</span>
            </ROW>
            <ROW>{patient.patientMedicalAidNumber} ({patient.patientMedicalAidDependantCode})</ROW>
            <ROW>
              <span className="text-muted">
                Last checked:{" "}
                {patient?.patientMedicalAidLastCheck?.trim()
                  ? formatDate(patient.patientMedicalAidLastCheck)
                  : "N/A"}
              </span>
            </ROW>
          </>
        ) : null}
      </Col>

      {/* Col 4 — Account info */}
      {account?._id && (
        <Col xs={12} md={4} className="mb-2 mb-md-0 mt-0">
          <div>
            {/* Heading row: ACCOUNT name + arrow */}
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold" style={{ lineHeight: "1.25" }}>
                ACCOUNT:
                <span className="fw-normal ms-2">
                  {account.accountNumber ? `${account.accountNumber} - ` : ""}
                  {[account.accountFullNames, account.accountLastName].filter(Boolean).join(" ")}
                </span>
              </span>
              {canOpenAccount && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Go to Account</Tooltip>}>
                <Button
                  variant="link"
                  className="p-0 text-secondary"
                  style={{ lineHeight: 1 }}
                  onClick={() => {
                    const fi = (patient.patientFullNames || patient.patientFirstName || '').charAt(0);
                    const pShort = `Patient - ${fi ? fi + ' ' : ''}${patient.patientLastName || ''}`.trim();
                    const hasAppointment = appointmentId && appointmentId !== '1' && appointment;
                    navigate(`/accounts/${account._id}`, {
                      state: hasAppointment
                        ? {
                            breadcrumbTrail: [
                              { label: 'Appointments', path: getBookingsReturnPath() },
                              { label: pShort, path: `/patients/appointment/${id}/${appointmentId}` },
                            ],
                          }
                        : {
                            fromPatient: true,
                            patientName: pShort,
                            patientId: patient._id,
                            ...(locationState?.fromDashboard ? { fromDashboard: true } : {}),
                          },
                    });
                  }}
                >
                  <i className="fa-solid fa-arrow-right fs-6 fw-bold" />
                </Button>
              </OverlayTrigger>
              )}
            </div>
            {canViewBalance && agedBuckets && (
              <div className="mt-2" style={{ fontSize: "0.75rem" }}>
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-primary text-center">
                    <tr>
                      <th className="text-primary fw-semibold" style={{width: "20%"}}></th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>Outstanding</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>30d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>60d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>90d</th>
                      <th className="text-primary fw-semibold" style={{width: "16%"}}>120d+</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Patient</td>
                      <td className="text-end text-danger fw-semibold">
                        R {(
                          (agedBuckets.days30?.patient || 0) +
                          (agedBuckets.days60?.patient || 0) +
                          (agedBuckets.days90?.patient || 0) +
                          (agedBuckets.days120plus?.patient || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">R {(agedBuckets.days30?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days60?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days90?.patient || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days120plus?.patient || 0).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Medical Aid</td>
                      <td className="text-end text-danger fw-semibold">
                        R {(
                          (agedBuckets.days30?.medicalAid || 0) +
                          (agedBuckets.days60?.medicalAid || 0) +
                          (agedBuckets.days90?.medicalAid || 0) +
                          (agedBuckets.days120plus?.medicalAid || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">R {(agedBuckets.days30?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days60?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days90?.medicalAid || 0).toFixed(2)}</td>
                      <td className="text-end">R {(agedBuckets.days120plus?.medicalAid || 0).toFixed(2)}</td>
                    </tr>
                    {unallocatedBalance > 0 && (
                      <tr>
                        <td className="fw-semibold">Unallocated</td>
                        <td className="fw-semibold text-success text-end">R {unallocatedBalance.toFixed(2)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Col>
      )}
    </>
  );
}

function EligibilityPane({ patient, account, eligibilityData, setEligibilityData }) {
  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 text-primary">Eligibility</h6>
          {patient?.patientMedicalAid ? (
            <CheckEligibility
              patient={patient}
              account={account}
              className="w-auto"
              onEligibility={(data) => setEligibilityData(data)}
            />
          ) : null}
        </div>

        {!patient?.patientMedicalAid ? (
          <Alert variant="warning" className="mb-0">
            Patient is not linked to a medical aid.
          </Alert>
        ) : eligibilityData ? (
          <EligibilitySummary eligibility={eligibilityData} />
        ) : (
          <div className="text-muted">No eligibility check performed yet.</div>
        )}
      </Card.Body>
    </Card>
  );
}

function CasesPane({ patient, currentUserId }) {
  // Render the panel directly (no Card wrapper). The Card.Body's 1rem padding
  // was pushing the "PATIENT CASES" heading ~16px below the row top, so it
  // sat below the "Patient Info" button in the left column. Stripping the
  // wrapper lets the heading share the row baseline.
  return (
    <CaseManagerPanel
      patientId={patient?._id}
      existingReferrers={EMPTY_REFERRERS}
      currentUserId={currentUserId}
    />
  );
}

export function Patientsappointment() {
  const { id, appointmentId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Stage 4 — auto-surface a ready transcription when the user lands on
  // (or returns to) this patient's page. pollPendingForPatient is idempotent
  // (seenJobIdsRef guard), so re-runs on appointmentId change are safe.
  //
  // The `recording` context value object reference churns on every audio
  // level / timer tick during recording, so we hold it in a ref and only
  // re-fire the effect when the patient id changes — otherwise this would
  // hammer the listJobs endpoint many times per second while recording.
  const recording = useRecordingActions();
  const recordingRef = useRef(null);
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  useEffect(() => {
    if (!id) return;
    recordingRef.current?.pollPendingForPatient?.(id);
  }, [id]);

  const [patient, setPatient] = useState(null);
  const [account, setAccount] = useState({});
  const [appointment, setAppointment] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);

  // Unified section state for the left rail. Patient sections
  // ("info" | "eligibility" | "cases") render page-owned panels; the session
  // sections (apptTemplate/historical/history/diagnosis/linkNotes/files/reports)
  // are delegated to <Session/>, which we drive as a controlled, nav-hidden child.
  // Default: land on the appointment notes when a practitioner opens a real
  // appointment; otherwise show the patient profile.
  const [activeSection, setActiveSection] = useState(() => {
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : null;
      const roles = Array.isArray(decoded?.role) ? decoded.role : [];
      const canDoNotes =
        roles.includes("Practitioner") || roles.includes("SuperAdmin");
      const realAppt = appointmentId && appointmentId !== "1";
      return canDoNotes && realAppt ? "apptTemplate" : "info";
    } catch {
      return "info";
    }
  });

  const [accountBalance, setAccountBalance] = useState(null);
  const [agedBuckets, setAgedBuckets] = useState(null);
  const [unallocatedBalance, setUnallocatedBalance] = useState(0);

  // Decode JWT once; derive every role/id flag from this single source.
  const userToken = useMemo(() => {
    try {
      const token = sessionStorage.getItem("user");
      if (!token) return null;
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, []);

  const userRole = useMemo(() => {
    const roles = Array.isArray(userToken?.role) ? userToken.role : [];
    return roles.includes("Practitioner") || roles.includes("SuperAdmin");
  }, [userToken]);

  const canViewBalance = useMemo(() => {
    const roles = Array.isArray(userToken?.role) ? userToken.role : [];
    return roles.includes("Finance") || roles.includes("SuperAdmin");
  }, [userToken]);

  // Gates the "Go to Account" jump into the permissioned Accounts area.
  // Without it the button would offer a destination that refuses to render.
  const canOpenAccount = useMemo(() => canViewAccounts(), []);

  const canAccessPatients = useMemo(() => {
    const roles = [
      ...(Array.isArray(userToken?.role) ? userToken.role : userToken?.role ? [userToken.role] : []),
      ...(Array.isArray(userToken?.permissions) ? userToken.permissions : []),
    ];
    return roles.some(r => ['SuperAdmin', 'Receptionist'].includes(r));
  }, [userToken]);

  const currentUserId = useMemo(() => userToken?._id || "", [userToken]);

  const refreshPatientData = useCallback(async () => {
    try {
      const p = await getPatientById(id);
      setPatient(p);
      if (p?._id) {
        const acc = await getAccountByPatientId(p._id);
        setAccount(acc);
      }
    } catch (e) {
      console.error("Error fetching patient/account:", e);
    }
  }, [id]);

  const { open: openEditPatient, Modal: EditPatientModalHost } =
    useEditPatientModal(refreshPatientData);

  useEffect(() => {
    if (id) refreshPatientData();
  }, [id, refreshPatientData]);

  useEffect(() => {
    if (!appointmentId || appointmentId === '1') return;
    let cancelled = false;
    getAppointmentById(appointmentId).then((data) => {
      if (!cancelled) setAppointment(data);
    }).catch((err) => console.error("Failed to load appointment for breadcrumb:", err));
    return () => { cancelled = true; };
  }, [appointmentId]);

  useEffect(() => {
    if (!account?._id || !canViewBalance) return;
    getAgedBalancesByAccount(account._id)
      .then((data) => {
        const buckets = data?.agedBalances || {};
        const total = Object.values(buckets).reduce(
          (sum, b) => sum + (b.patient || 0) + (b.medicalAid || 0),
          0
        );
        setAccountBalance(total);
        setAgedBuckets(buckets);
        setUnallocatedBalance(data?.unallocatedTotal || 0);
      })
      .catch(() => { setAccountBalance(null); setAgedBuckets(null); setUnallocatedBalance(0); });
  }, [account?._id, canViewBalance]);

  const breadcrumbItems = useMemo(() => {
    if (!patient) return null;
    const firstInitial = (patient.patientFullNames || patient.patientFirstName || '').charAt(0);
    const shortName = `Patient - ${firstInitial ? firstInitial + ' ' : ''}${patient.patientLastName || ''}`.trim();
    const apptShortName = `${firstInitial ? firstInitial + ' ' : ''}${patient.patientLastName || ''}`.trim();

    if (sessionId) {
      // PAGE-08: Session Notes — Appointments > [Date] - [Patient Name] > Session Notes
      if (!appointment) return null;
      const appointmentLabel = `${formatBreadcrumbDate(appointment.start)} \u2013 ${apptShortName}`;
      return [
        { label: 'Appointments', path: getBookingsReturnPath() },
        { label: appointmentLabel, path: `/patients/appointment/${id}/${appointment._id || appointmentId}` },
        { label: 'Session Notes' },
      ];
    }

    if (appointmentId && appointmentId !== '1') {
      // PAGE-07: Appointment Detail — Appointments > [Date] - [Patient Name]
      if (!appointment) return null;
      const appointmentLabel = `${formatBreadcrumbDate(appointment.start)} \u2013 ${apptShortName}`;
      return [
        { label: 'Appointments', path: getBookingsReturnPath() },
        { label: appointmentLabel },
      ];
    }

    // PAGE-01: Patient Profile — upstream trail > [Patient Name]
    if (location.state?.breadcrumbTrail?.length) {
      return [...location.state.breadcrumbTrail, { label: shortName }];
    }
    if (location.state?.fromDashboard) {
      return [
        { label: 'Dashboard', path: '/home' },
        { label: shortName },
      ];
    }
    return [
      { label: 'Patients', path: '/patients', canAccess: canAccessPatients },
      { label: shortName },
    ];
  }, [patient, appointment, id, appointmentId, sessionId, canAccessPatients, location.state]);

  const hasAppointment = appointmentId && appointmentId !== "1";
  const canSeeClinicalHistory = userCanSeeClinicalHistory();

  const PATIENT_KEYS = ["info", "eligibility", "cases"];
  const SESSION_KEYS = [
    "apptTemplate", "apptNotepad", "apptMedia", "historical",
    "history", "diagnosis", "linkNotes", "files", "reports",
  ];
  const isPatientSection = PATIENT_KEYS.includes(activeSection);
  const isSessionSection = SESSION_KEYS.includes(activeSection);

  // Left-rail items: patient actions first, then the session sections
  // (practitioner-only). "edit" is an action (opens a modal), not a section.
  const railItems = [
    { key: "info", label: "Patient Info", short: "Info", icon: "fa-light fa-user", kind: "patient" },
    { key: "edit", label: "Edit Patient", short: "Edit", icon: "fa-light fa-user-pen", kind: "action", onClick: () => openEditPatient(patient?._id) },
    ...(patient?.patientMedicalAid
      ? [{ key: "eligibility", label: "Check Patient", short: "Check", icon: "fa-light fa-stethoscope", kind: "patient" }]
      : []),
    { key: "cases", label: "Patient Cases", short: "Cases", icon: "fa-light fa-briefcase-medical", kind: "patient" },
    ...(userRole
      ? [
          { key: "apptTemplate", label: hasAppointment ? "Appointment Notes" : "Add Note", short: hasAppointment ? "Notes" : "Add Note", icon: "fa-light fa-note-medical", kind: "session", matches: ["apptTemplate", "apptNotepad", "apptMedia"] },
          { key: "historical", label: "Previous Notes", short: "Previous", icon: "fa-sharp fa-light fa-notes", kind: "session" },
          ...(canSeeClinicalHistory ? [{ key: "history", label: "AI History", short: "History", icon: "fa-light fa-clock-rotate-left", kind: "session" }] : []),
          { key: "diagnosis", label: "Diagnoses", short: "Diagnoses", icon: "fa-sharp fa-light fa-user-doctor-message", kind: "session" },
          { key: "linkNotes", label: "Link Notes", short: "Link", icon: "fa-light fa-paperclip", kind: "session" },
          { key: "files", label: "Patient Files", short: "Files", icon: "fa-light fa-cabinet-filing", kind: "session" },
          { key: "reports", label: "Generate Reports", short: "Reports", icon: "fa-light fa-file-arrow-down", kind: "session" },
        ]
      : []),
  ];

  const isRailActive = (item) =>
    item.matches ? item.matches.includes(activeSection) : activeSection === item.key;

  const onRailClick = (item) => {
    if (item.kind === "action") { item.onClick?.(); return; }
    setActiveSection(item.key);
  };

  // Session drives its own internal transitions (e.g. save → Previous Notes).
  // Reflect those in the rail highlight, but only while a session section is
  // already showing — don't let a background event yank the user off a patient
  // panel they're reading.
  const handleSessionSectionChange = (k) => {
    if (!k) return;
    setActiveSection((prev) => (PATIENT_KEYS.includes(prev) ? prev : k));
  };

  const renderPatientContent = () => {
    if (activeSection === "eligibility") {
      return (
        <EligibilityPane
          patient={patient}
          account={account}
          eligibilityData={eligibilityData}
          setEligibilityData={setEligibilityData}
        />
      );
    }
    if (activeSection === "cases") {
      return <CasesPane patient={patient} currentUserId={currentUserId} />;
    }
    return (
      <Row className="gx-3" style={{ fontSize: "0.875rem" }}>
        <PatientInfoCard
          patient={patient}
          account={account}
          agedBuckets={agedBuckets}
          unallocatedBalance={unallocatedBalance}
          canViewBalance={canViewBalance}
          canOpenAccount={canOpenAccount}
          appointmentId={appointmentId}
          appointment={appointment}
          id={id}
          locationState={location.state}
          navigate={navigate}
        />
      </Row>
    );
  };

  const renderRail = (variant) => (
    <div
      className={variant === "mobile" ? "acc-rail-mobile" : "acc-rail"}
      style={variant === "mobile" ? undefined : { position: "sticky", top: 55 }}
    >
      {railItems.map((item) => (
        <Button
          key={item.key}
          aria-label={item.label}
          className={`acc-rail-item acc-rail-item-labeled${isRailActive(item) ? " active" : ""}`}
          onClick={() => onRailClick(item)}
        >
          <i className={item.icon} />
          <span className="acc-rail-label">{item.short}</span>
        </Button>
      ))}
    </div>
  );

  const patientNameHeader = (
    <h5 className="text-primary fw-semibold mb-0">
      {patient ? (
        <>
          {`${patient.patientTitle || ""} ${patient.patientFullNames || ""} ${patient.patientLastName || ""}`.trim()}
          {patient.patientIODFund ? (
            (patient.patientIODFundCategory || "").toUpperCase() === "INSURANCE" ? (
              <sup><span className="badge bg-info text-dark ms-2" title="Insurance" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>I</span></sup>
            ) : (
              <sup><span className="badge bg-warning text-dark ms-2" title="IOD" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>I</span></sup>
            )
          ) : patient.patientMedicalAid ? (
            <sup><span className="badge bg-primary ms-2" title="Medical Aid" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>M</span></sup>
          ) : (
            <sup><span className="badge bg-secondary ms-2" title="Private" style={{ fontSize: "0.55rem", verticalAlign: "super" }}>P</span></sup>
          )}
          {patient.patientMedicalAid && (
            <span
              className={`ms-1 ${(patient.patientMedicalAidStatus || "").toLowerCase().includes("active") ? "text-success" : "text-danger"}`}
              title={`${patient.patientMedicalAidStatus || "Unknown"}${patient.patientMedicalAidJoinDate ? ` | Joined: ${patient.patientMedicalAidJoinDate}` : ""}`}
              style={{ fontSize: "0.55rem", verticalAlign: "super", cursor: "help" }}
            >
              <i className="fa-duotone fa-light fa-wave-pulse fs-6"></i>
            </span>
          )}
        </>
      ) : "Loading..."}
    </h5>
  );

  return (
    // Layout mirrors Accounts.jsx so the icon rail sits at the same X/Y across
    // pages (no jump on navigation): rail is the first column and spans the
    // full page height; the breadcrumb + patient name live inside the content
    // column beside it.
    <div style={{ marginTop: "50px" }}>
      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<lg) — horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-lg-none g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              {renderRail("mobile")}
            </Container>
          </Col>

          {/* LEFT PANEL — DESKTOP ICON RAIL (>=lg). White container stretches
              the full page length; the rail inside is sticky at top:55. */}
          <Col xs="auto" className="d-none d-lg-block g-0" style={{ width: 64 }}>
            <Container
              className="bg-white g-0 p-1 shadow-sm rounded h-100"
              style={{ width: 64, minHeight: "100vh" }}
            >
              {renderRail("desktop")}
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 pb-3 mb-3">
              {breadcrumbItems && (
                <div className="ms-1 mb-2">
                  <Breadcrumb items={breadcrumbItems} />
                </div>
              )}

              {/* Patient name — always pinned at the top of the content column */}
              <div className="ms-1 mb-3">{patientNameHeader}</div>

              {isPatientSection && (
                <div className="acc-list">{renderPatientContent()}</div>
              )}
              {/* Session stays mounted (hidden) while a patient section shows so
                  an in-progress recording / RecordModal isn't torn down. */}
              {userRole && (
                <div className={isPatientSection ? "d-none" : ""}>
                  <Session
                    patient={patient}
                    appointment={appointment}
                    hideNav
                    controlledSection={isSessionSection ? activeSection : undefined}
                    onSectionChange={handleSessionSectionChange}
                  />
                </div>
              )}
            </Container>
          </Col>
        </Row>
      </Container>

      <EditPatientModalHost />
    </div>
  );
}
